import { useRef, useEffect, useCallback, useMemo } from "react";

import Chart from "chart.js/auto";

import Show from "../../utility/Show";

import {
  getFeatureIcon,
  getTabColor,
  getFeelLineLegend2,
  legendWalkIndexDescription,
  getDescription,
  getMainCategory,
} from "../../services"; 

const colors = ["#F26F01", "#FF77C8", "#F5C500", "#3AA7F0", "#9A77CF"];

const colorShades = [
  {
    type: 'food',
    colors: ['#f26f01', '#ff8501', '#ff9601', '#bf5700', '#a54b00']
  },
  {
    type: 'service',
    colors: ['#ff77c8', '#cc5f9f', '#b2538b', '#7e3b63', '#652f4f']
  },
  {
    type: 'shops',
    colors: ['#f5c500', '#ffef00', '#ffde00', '#c29b00', '#a88700']
  },
  {
    type: 'transit',
    colors: ['#3aa7f0', '#3d97ff', '#3d8aff', '#3083cc', '#2a72b2']
  },
  {
    type: 'leisure',
    colors: ['#9a77cf', '#bd92ff', '#b68cf5', '#74599c', '#614b82']
  }
];

const MustHavesGraph = ({ state, dispatch, actions, className="", filtersOnly=false }) => {
  const {
    currentTab,
    mapTab,
    showFilter,
    showMarkerList,
    lifeStyleCompareMaps,
    lifeStyleScores,
    walkindexScores,
    compareLifeStyleAddress,
    isMobile,
    showCompareGraph,
    categoryFilters,
    listOfMarkers,
    compareAddress,
  } = state;

  const filteredCategoryFilters = useMemo(
    () => categoryFilters.filter(f => typeof f === "string" && f.trim() !== ""),
    [categoryFilters]
  );

  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const isWalkIndex = currentTab === 'walkindex';
  const isAll = mapTab === 'All';

  const filterColors = useMemo(() => {
    // Track used colors for each main category to avoid repeats
    const usedColors = {};
    // Track all used colors globally to avoid any repeats
    const allUsedColors = new Set();

    return filteredCategoryFilters.map(filter => {
      const mainCategory = getMainCategory(filter);
      console.log('mainCategory', mainCategory, filter);
      const shadeObj = colorShades.find(c => c.type === mainCategory);
      if (!shadeObj) return "#888"; // fallback color

      // Find an unused color for this main category
      let color;
      for (let i = 0; i < shadeObj.colors.length; i++) {
        const candidate = shadeObj.colors[i];
        if (!allUsedColors.has(candidate)) {
          color = candidate;
          usedColors[mainCategory] = usedColors[mainCategory] || [];
          usedColors[mainCategory].push(candidate);
          allUsedColors.add(candidate);
          break;
        }
      }
      // If all colors are used, fallback to the first color
      if (!color) {
        color = shadeObj.colors[0];
      }
      return color;
    });
  }, [filteredCategoryFilters]);

  const isSearchInList = useCallback((datasetIndex) => {
    return filteredCategoryFilters.reduce((count, filter) => {
      if (!listOfMarkers[datasetIndex]) return 0;
      listOfMarkers[datasetIndex]?.some((marker) => {
        const category = actions.normalizeCategories(marker.category || 'others');
        const name = actions.normalizeCategories(marker.name || '');
        // Check for combined word matches (e.g., "drug store" vs "drugstore")
        const isCombinedMatch =
          actions.isCombinedWordMatch(category, filter) ||
          actions.isCombinedWordMatch(name, filter);
        if (name.includes(filter) || category.includes(filter) || isCombinedMatch) {
          count += 1;
          return true;
        }
        return false;
      });
      return count;
    }, 0);
  }, [filteredCategoryFilters, listOfMarkers, categoryFilters, compareAddress]);

  const datasets = useMemo(() => {
    // If filtersOnly, each bar is a filteredCategoryFilter, not per address
    if (filtersOnly) {
        // CRITICAL CHANGE: Return multiple datasets, one for each segment/filter.
        return filteredCategoryFilters.map((filter, index) => {
            
            // 1. Calculate the total count for this specific filter across all marker groups
            const totalCount = listOfMarkers.reduce((acc, markerGroup) => {
              if (!markerGroup) return acc;
              return (
                acc +
                markerGroup.filter(
                  marker =>
                    marker.category?.toLowerCase().includes(filter.toLowerCase()) ||
                    marker.name?.toLowerCase().includes(filter.toLowerCase())
                ).length
              );
            }, 0);

            // 2. Return a dataset object for this segment
            return {
              label: filter, // The label for the tooltip
              data: [totalCount], // CRITICAL: Single data point array
              backgroundColor: filterColors[index],
              // CRITICAL: This property makes the segments stack horizontally
              stack: 'single-horizontal-bar', 
              barThickness: 50,
            };
        });
        
    }

    // Default: each filter is a dataset, each address is a bar
    return filteredCategoryFilters
      .map((filter, i) => {
        const data = listOfMarkers.map((markerGroup) => {
          const count = markerGroup?.filter(
            (marker) =>
              marker.category?.toLowerCase().includes(filter.toLowerCase()) ||
              marker.name?.toLowerCase().includes(filter.toLowerCase())
          ).length;
          return count;
        });
        return {
          label: filter,
          data,
          backgroundColor: filterColors[i],
        };
      })
      .filter((dataset) => dataset.data.some((count) => count > 0));
  }, [filteredCategoryFilters, listOfMarkers, categoryFilters, filtersOnly, filterColors, compareAddress]);

  const chartLabels = useMemo(() => {
    if (filtersOnly) {
      return filteredCategoryFilters;
    }
    return ['A1', 'A2', 'A3', 'A4'];
  }, [filtersOnly, filteredCategoryFilters]);

  useEffect(() => {
    const ctx = chartRef?.current?.getContext("2d");

    // If the chart instance already exists, update it
    if (chartInstance.current) {
      chartInstance.current.data.labels = chartLabels;
      chartInstance.current.data.datasets = datasets;
      chartInstance.current.update(); // Update the chart with new data
    } else {
      // Create a new chart instance
      chartInstance.current = new Chart(ctx, {
        type: "bar",
        data: {
          labels: filtersOnly ? [''] : chartLabels,
          datasets: datasets,
        },
        options: {
          indexAxis: filtersOnly ? "y" : "x",
          datasets: {
            bar: {
              minBarLength: 5, // pixels
            },
          },
          responsive: true,
          layout: {
            padding: {
              top: 20, // Add padding to the top
            },
          },
          plugins: {
            legend: {
              display: false
            },
            title: {
              display: false,
            },
            tooltip: {
              // Enable tooltips
              displayColors: !isWalkIndex,
              callbacks: {
                title: (tooltipItems) => {
                  if (filtersOnly) return ""; // Hide title if filtersOnly is true
                  const index = tooltipItems[0].dataIndex;
                  // Customize the tooltip title (e.g., bar label)
                  return `${compareLifeStyleAddress[index]?.shortName || compareLifeStyleAddress[index]?.property}`;
                },
                label: (tooltipItem) => {
                  const datasetLabel = tooltipItem.dataset.label || ""; // Dataset label
                  const value = tooltipItem.raw; // Bar value

                  const legendDescription = isWalkIndex ? getLegendDescription(tooltipItem.raw) : '';
                  const additionalDescription = isWalkIndex && mapTab !== 'Comfort' ? getDescription(isWalkIndex, mapTab) : '';
                  const valueDescription = !isWalkIndex ? `${datasetLabel}: ${value}` : '';

                  return `${legendDescription} ${additionalDescription}${valueDescription}`;
                }
              },
            },
            datalabels: {
              color: '#ffffff',
              display: (context) => {
                // Only display if value is not zero
                return context.dataset.data[context.dataIndex] > 0;
              },
              anchor: 'center',
              align: 'center',
              position: 'center',
            }
          },
          scales: {
            x: {
              stacked: true,
              display: !filtersOnly ? true : false,
              color: "#fff",
              grid: {
                display: false,
              },
              border: {
                color: "white",
              },
              ticks: {
                color: "#fff",
                font: {
                  size: 12,
                  family: "Montserrat",
                },
              },
              title: {
                display: false,
              },
            },
            y: {
              stacked: true,
              display: !filtersOnly ? true : false,
              border: {
                display: true,
                color: "white", // Axis border color
                dash: function (context) {
                  // Dynamically set dash pattern based on tick value
                  const tick = context.tick.value;
                  if (tick === 3) return []; // Solid line (no dash)
                  if (tick === 2) return [10, 5]; // Dashed line
                  if (tick === 1) return [3, 3]; // Dotted line
                  return []; // Default for other ticks (solid)
                },
              },
              ticks: {
                color: "#fff",
                font: {
                  size: 14,
                  family: "Montserrat",
                },
              },
              // max: Math.max(...getYAxisTicks()),
              min: 0,
              title: {
                display: false,
              },
              grid: {
                display: isWalkIndex ? true : false,
                drawBorder: true,
                drawTicks: true,
                drawOnChartArea: true,
                z: 1,
                color: function (context) {
                  const tick = context.tick.value;
                  if (tick === 3) return "#ffffff";
                  if (tick === 2) return "#ffffff";
                  if (tick === 1) return "#ffffff";
                  // if (tick === 1) return findColor('walkindex', mapTab);
                  return "transparent"; // Hide other grid lines
                },
                lineWidth: function (context) {
                  const tick = context.tick.value;
                  if ([3, 2, 1].includes(tick)) return 2; // Thicker line for styled ticks
                  return 0; // No line for other ticks
                },
              },
            },
          },
          onClick: (event, activeElements) => {
            if (activeElements.length > 0) {
              const chart = chartInstance.current; // Reference to the chart instance
              const datasetIndex = activeElements[0].datasetIndex; // Dataset index
              const dataIndex = activeElements[0].index; // Data point index
      
              // Access the clicked data
              const clickedLabel = chart.data.labels[dataIndex]; // X-axis label
              const clickedValue = chart.data.datasets[datasetIndex].data[dataIndex]; // Data value
              const clickedDataset = chart.data.datasets[datasetIndex].label; // Dataset label
              // handleShowEstablishments(dataIndex, clickedDataset)
            }
          },
        },
        plugins: [
          {
            id: "addText",
            afterDatasetsDraw: (chart) => {
              const ctx = chart.ctx;
              const datasets = chart.data.datasets;
        
              if (!isWalkIndex) {
                datasets.forEach((dataset, datasetIndex) => {
                  if (datasets.length - 1 === datasetIndex) {
                    dataset.data.forEach((_, index) => {
                      let accumulatedValue = 0;
                      // Calculate the sum of values for this index across all datasets
                      datasets.forEach((innerDataset) => {
                        accumulatedValue += innerDataset.data[index] || 0;
                      });
          
                      // Get bar details for the current index
                      const meta = chart.getDatasetMeta(datasetIndex);
                      const bar = meta.data[index];
          
                      if (bar && accumulatedValue > 0) {
                        const x = bar.x; // X position of the bar
                        const y = bar.y; // Y position (top of the bar)
          
                        ctx.save();
                        ctx.font = "bold 14px Montserrat";
                        ctx.fillStyle = "#fff"; // Set text color
                        ctx.textAlign = "center"; // Center align the text
                        if (!filtersOnly) {
                          ctx.fillText(`${isSearchInList(index)}/${categoryFilters?.length}`, x, y - 8); // Draw the accumulated value above the bar
                        }
                        ctx.restore();
                      }
                    });
                  }
                });
              }
            },
          },
        ]
      });
    }

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [
    lifeStyleCompareMaps,
    datasets,
    chartLabels,
    listOfMarkers,
    categoryFilters,
    compareLifeStyleAddress,
    compareAddress,
  ]);

  const getLegendDescription = (index) => {
    const scores = index;
    const finalType = mapTab?.toLowerCase() === 'energy' ? 'activities' : mapTab?.toLowerCase();
  
    const getDescription = (descriptionSource) => {
      return descriptionSource?.[finalType]?.[(scores || 1) - 1];
    };
  
    return getDescription(legendWalkIndexDescription)
  };

  const renderVersion2FeelLegend = () => {
    return (
      <div className={`flex flex-col w-full ${
          isMobile ? 'mt-0' : 'mt-2'
        }`}
      >
        {[3, 2, 1].map((index) => (
          <div
            key={index}
            className="map-type-legend flex justify-start sm:justify-between items-center gap-x-3 text-center text-xs font-normal"
            data-border-style={index}
            style={getTabColor(isWalkIndex, mapTab)}
          >
            {getFeelLineLegend2(index)}
            <div className="text-right">{getLegendDescription(index)}</div>
          </div>
        ))}
      </div>
    )
  };

  const renderChartLegends = () => {
    return (
      <div className="flex flex-col space-y-2">
      {filteredCategoryFilters.map((filter, index) => (
        <div key={filter+index} className="flex items-center">
        <span
          className="w-2 h-2"
          style={{ backgroundColor: filterColors[index] }}
        ></span>
        <span className="ml-2 text-white text-xs">
          {filter.charAt(0).toUpperCase() + filter.slice(1)}
        </span>
        </div>
      ))}
      </div>
    );
  };

  return (
    <div className={`graph-wrapper py-4 px-4 ${showFilter || showMarkerList ? 'hide-graph' : ''} ${className}`}>
      <h2 className='flex justify-between gap-x-4 font-bold text-white'>
        <Show>
          <Show.When isTrue={isMobile && showCompareGraph}>
            <div className='flex gap-x-2 chart-legend w-fit text-sm' style={getTabColor(isWalkIndex, mapTab)}>
              {getFeatureIcon(isWalkIndex, mapTab)}
              {mapTab}
            </div>
          </Show.When>
          <Show.Else>
            Must-Haves
          </Show.Else>
        </Show>
        <Show.When isTrue={!isAll}>
          <div className='flex flex-col items-end'>
            <Show.When isTrue={!isMobile}>
              <div className='flex gap-x-2 items-center chart-legend w-fit text-sm' style={getTabColor(isWalkIndex, mapTab)}>
                {getFeatureIcon(isWalkIndex, mapTab)}
                {mapTab}
              </div>
            </Show.When>
            <Show.When isTrue={isWalkIndex}>
              {renderVersion2FeelLegend()}
            </Show.When>
          </div>
        </Show.When>
      </h2>
      <div>
        <div
          className={`flex justify-between items-end mb-2 ${
            mapTab === 'All' ? '-mt-8' : 'mt-0'
          } ${
            isMobile && !isWalkIndex ? '-mt-4' : ''
          }`}
        >
          <div className="w-[130px] text-xs mb-2"></div>
          {renderChartLegends()}
        </div>
        <div className={`w-full h-auto ${filtersOnly ? 'max-h-[150px]' : 'max-h-[350px]'}`}>
          <canvas
            ref={chartRef}
            className="w-full"
            style={{
              minHeight: filtersOnly ? 70 : 220,
              height: filtersOnly ? 70 : 220,
              maxHeight: 350
            }} 
          />
        </div>
      </div>
    </div>
  )
}

export default MustHavesGraph;